import React, { FC, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { TestCard as BaseTestCard } from "@natera/patient";
import { Utils } from "@natera/material/lib/date";
import { Button } from "@natera/form";
import classNames from "classnames";
import * as R from "ramda";
import CancerTypeBlock from "@app/components/cancerTypeBlock";

import {
  ParsedTestCard,
  PrimaryAction,
  TestCardStatus,
  TestType,
} from "@app/provider/testData/types";
import {
  ConfigContext,
  NotificationContext,
  ViewResultContext,
} from "@app/provider";
import { HEAP_EVENTS } from "@app/provider/types";
import { routes } from "@app/routing";
import {
  getTestNameForHeapByTestType,
  openNewWindow,
  snakeCaseToCapitalizedWords,
} from "@app/utils";
import { getStatusText, resolveI18n } from "@app/provider/testData/utils";
import { UrlVideoType } from "@app/pages/private/simpleOrder/aboutYourTest/aboutYourTest";
import { TestCardTitle } from "@app/components/testCard/testCardComponents";
// PNG @2x (418×418) with alpha channel — the sparkle pattern behind the
// journey panel. Rendered by CSS `background-size: 209px 209px` to its
// intended visual size. PNG uses webpack's default asset/inline pipeline
// (baked as base64 in the bundle at ~136 KB) so the browser cache path
// piggybacks on the bundle's own caching.
import TestCardPattern from "@assets/images/testCardPattern.png";
import "./style.scss";

const messages = defineMessages({
  testCardOrdered: {
    id: "testCardOrdered",
    defaultMessage: "Ordered on {date}",
  },
  testCardSampleWasReceived: {
    id: "testCardSampleWasReceived",
    defaultMessage: "On {sampleReceivedDate}",
  },
  testCardResultReceived: {
    id: "testCardResultReceived",
    defaultMessage: "Results received on {date}",
  },
  testCardCancelled: {
    id: "testCardCancelled",
    defaultMessage: "Cancelled on {date}",
  },
  testCardExpired: {
    id: "testCardExpired",
    defaultMessage: "Expired on {date}",
  },
  testCardCurrent: {
    id: "testCardCurrent",
    defaultMessage: "Current",
  },
  testCardNext: {
    id: "testCardNext",
    defaultMessage: "Next",
  },
  testCardActionSchedule: {
    id: "testCardActionSchedule",
    defaultMessage: "Schedule a Draw",
  },
  testCardActionTrack: {
    id: "testCardActionTrack",
    defaultMessage: "Track Kit Status",
  },
  testCardActionAppointment: {
    id: "testCardActionAppointment",
    defaultMessage: "Appointment Detail",
  },
  testCardActionResult: {
    id: "testCardActionResult",
    defaultMessage: "View Your Results",
  },
  testCardActionCompleteOrder: {
    id: "testCardActionCompleteOrder",
    defaultMessage: "Complete Order",
  },
  testCardViewTestDetails: {
    id: "testCardViewTestDetails",
    defaultMessage: "View Test Details",
  },
  testCardViewDetails: {
    id: "testCardViewDetails",
    defaultMessage: "View details",
  },
  testCardRecurring: {
    id: "testCardRecurring",
    defaultMessage: "Recurring",
  },
  testCardKitTrackingNotAvailable: {
    id: "testCardKitTrackingNotAvailable",
    defaultMessage:
      "We're sorry. Kit tracking information is not available at the moment.",
  },
});

const HEAP_CTA_EVENTS_TEST_TYPES = [
  TestType.RENASIGHT,
  TestType.PROSPERA,
  TestType.PANORAMA,
  TestType.HORIZON,
  TestType.EMPOWER,
  TestType.VISTARA,
  TestType.SIGNATERA,
  TestType.SINGLE_GENE_NIPT,
  TestType.ALTERA,
];

export interface TestCardProps {
  testCard: ParsedTestCard;
  showPrimaryAction?: boolean;
  leftSideClickable?: boolean;
  isHistory?: boolean;
}

const TestCard: FC<TestCardProps> = ({
  testCard,
  showPrimaryAction = true,
  leftSideClickable,
  isHistory,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useContext(ConfigContext);
  const { addNotification } = useContext(NotificationContext);
  const { openResultPage, openTestResultDetailsPage } =
    useContext(ViewResultContext);

  const historyNavOpts = isHistory
    ? { state: { from: routes.testHistory } }
    : undefined;

  const handleCompleteOrder = (orderUidPublic: string) => {
    const urlVideoType = testCard.isComboOrder
      ? UrlVideoType.COMBO_ORDER
      : testCard.testType;
    navigate(
      routes.aboutTestPage(orderUidPublic, urlVideoType),
      historyNavOpts
    );
  };

  const openTestDetails = (orderUidPublic: string, testUid: string) => {
    navigate(routes.testDetailsPage(orderUidPublic, testUid), historyNavOpts);
  };

  const handleScheduleDraw = (orderUidPublic: string, testUid: string) => {
    navigate(routes.sampleDrawPage(orderUidPublic, testUid), historyNavOpts);
  };

  const viewResults = () => openResultPage(testCard, isHistory);

  const openTestResultDetails = () =>
    openTestResultDetailsPage(testCard, isHistory);

  const trackKitStatus = () =>
    testCard.trackingNumber
      ? openNewWindow(`${config.links.TRACKING}${testCard.trackingNumber}`)
      : addNotification({
          type: "error",
          message: intl.formatMessage(messages.testCardKitTrackingNotAvailable),
        });

  const primaryActionParams = useMemo(
    () => ({
      [PrimaryAction.SCHEDULE_A_DRAW]: {
        title: intl.formatMessage(messages.testCardActionSchedule),
        action: handleScheduleDraw,
      },
      [PrimaryAction.TRACK_KIT_STATUS]: {
        title: intl.formatMessage(messages.testCardActionTrack),
        action: trackKitStatus,
      },
      [PrimaryAction.APPOINTMENT_DETAIL]: {
        title: intl.formatMessage(messages.testCardActionAppointment),
        action: openTestDetails,
      },
      [PrimaryAction.VIEW_YOUR_RESULTS]: {
        title: intl.formatMessage(messages.testCardActionResult),
        action: viewResults,
      },
      [PrimaryAction.VIEW_RESULT_PAGE]: {
        title: intl.formatMessage(messages.testCardActionResult),
        action: openTestResultDetails,
      },
      [PrimaryAction.COMPLETE_ORDER]: {
        title: intl.formatMessage(messages.testCardActionCompleteOrder),
        action: handleCompleteOrder,
      },
      [PrimaryAction.VIEW_TEST_DETAILS]: {
        title: intl.formatMessage(messages.testCardViewTestDetails),
        action: openTestDetails,
      },
    }),
    [testCard, intl, isHistory]
  );

  const renderPrimaryAction = () => {
    const primaryAction = testCard.primaryAction;
    if (!primaryAction) return null;

    const currentPrimaryAction = primaryActionParams[primaryAction];

    const action = () => {
      const allowHeapEventForTestType = R.includes(
        testCard.testType,
        HEAP_CTA_EVENTS_TEST_TYPES
      );

      if (allowHeapEventForTestType) {
        const cta =
          testCard.primaryAction === PrimaryAction.VIEW_RESULT_PAGE
            ? PrimaryAction.VIEW_YOUR_RESULTS
            : testCard.primaryAction;

        heap.track(HEAP_EVENTS.upp_click_testcardcta, {
          test_name: getTestNameForHeapByTestType(testCard.testType),
          cta: snakeCaseToCapitalizedWords(cta),
          lims_clinic_id: testCard.clinic?.limsId,
          public_order_uid: testCard.orderUidPublic,
          draw_coordinator: testCard.drawCoordinator,
        });
      }

      return currentPrimaryAction.action(testCard.orderUidPublic, testCard.uid);
    };

    return (
      <Button unelevated onClick={action} data-automation_id="test_card_button">
        {currentPrimaryAction.title}
      </Button>
    );
  };

  // Hide the outlined "View details" button when:
  //   1. The primary action already lands on the same test-details page
  //      (VIEW_TEST_DETAILS / APPOINTMENT_DETAIL) — redundant CTA.
  //   2. The card is being rendered on the test-details page itself
  //      (e.g. detailsRecord.tsx). Mirrors the title-link deactivation that
  //      existed before View details replaced the title click.
  const detailsPath = routes.testDetailsPage(
    testCard.orderUidPublic,
    testCard.uid
  );
  const isOnDetailsPage = location.pathname.startsWith(detailsPath);
  const renderViewDetails = () => {
    if (
      isOnDetailsPage ||
      testCard.primaryAction === PrimaryAction.VIEW_TEST_DETAILS ||
      testCard.primaryAction === PrimaryAction.APPOINTMENT_DETAIL
    ) {
      return null;
    }
    // aria-label mirrors the visible button text so audit tools see the
    // accessible name spelled out explicitly on the element. The
    // descriptive per-test aria ("Renasight test details" etc.) lives on
    // the clickable title Link so screen readers still get the
    // destination-focused announcement there.
    return (
      <Button
        outlined
        onClick={() => openTestDetails(testCard.orderUidPublic, testCard.uid)}
        aria-label={intl.formatMessage(messages.testCardViewDetails)}
        data-automation_id="test_card_view_details"
      >
        {intl.formatMessage(messages.testCardViewDetails)}
      </Button>
    );
  };

  const renderRecurringChip = () =>
    testCard.isSignateraSubsequent ? (
      <div className="upp-test-card__chip" aria-label="Recurring">
        <svg
          className="upp-test-card__chip-icon"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M17.65 6.35A7.958 7.958 0 0 0 12 4a8 8 0 1 0 7.75 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35Z"
            fill="currentColor"
          />
        </svg>
        <span className="upp-test-card__chip-label">
          {intl.formatMessage(messages.testCardRecurring)}
        </span>
      </div>
    ) : null;
  const renderDate = () => {
    if (
      [TestCardStatus.CANCELLED_TEST, TestCardStatus.EXPIRED_TEST].includes(
        testCard.status
      ) &&
      testCard.cancelledAt
    ) {
      const date = Utils.getUSLocaleDate(new Date(testCard.cancelledAt));
      const message =
        testCard.status === TestCardStatus.EXPIRED_TEST
          ? messages.testCardExpired
          : messages.testCardCancelled;
      return intl.formatMessage(message, { date });
    }
    if (
      testCard.status === TestCardStatus.REDRAW_NEEDED ||
      testCard.status === TestCardStatus.NEW_TISSUE_NEEDED ||
      testCard.status === TestCardStatus.UPCOMING_TEST
    ) {
      return intl.formatMessage(messages.testCardOrdered, {
        date: Utils.getUSLocaleDate(new Date(testCard.createdAt)),
      });
    }
    if (
      testCard.resultReleasedAt &&
      (testCard.status === TestCardStatus.RESULT_READY ||
        testCard.status === TestCardStatus.TEST_COMPLETE)
    ) {
      return intl.formatMessage(messages.testCardResultReceived, {
        date: Utils.getUSLocaleDate(new Date(testCard.resultReleasedAt)),
      });
    }
    return intl.formatMessage(messages.testCardOrdered, {
      date: Utils.getUSLocaleDate(new Date(testCard.createdAt)),
    });
  };

  const renderBottomLine = () =>
    resolveI18n(intl, testCard.i18nKeys, "bottomLine") ?? renderDate();

  const renderSecondaryStatusText = () => {
    const resolved = resolveI18n(
      intl,
      testCard.i18nKeys,
      "secondaryStatusText"
    );
    if (resolved) return resolved;
    // Legacy fallback - remove once all cards are rules-based
    if (
      [
        TestCardStatus.SAMPLE_RECEIVED,
        TestCardStatus.SAMPLES_RECEIVED,
      ].includes(testCard.status) &&
      testCard.sampleReceivedDate
    ) {
      return intl.formatMessage(messages.testCardSampleWasReceived, {
        sampleReceivedDate: Utils.getUSLocaleDate(
          new Date(testCard.sampleReceivedDate)
        ),
      });
    }
    return null;
  };

  return (
    <div
      className="upp-test-card"
      style={
        {
          "--upp-test-card-pattern": `url("${TestCardPattern}")`,
        } as React.CSSProperties
      }
    >
      <BaseTestCard
        title={
          <div className="upp-test-card__title-row">
            <TestCardTitle
              testCard={testCard}
              leftSideClickable={leftSideClickable}
              isHistory={isHistory}
            />
            {/* Recurring chip intentionally disabled for now — leave
                renderRecurringChip defined above for easy re-enable. */}
            {/* {renderRecurringChip()} */}
          </div>
        }
        description={
          <CancerTypeBlock
            iconOnly
            cancerType={testCard.clinicalInfo?.cancerType}
            dateOfSurgery={testCard.clinicalInfo?.dateOfSurgery}
          />
        }
        bottomLine={renderBottomLine()}
        expandable={false}
        childrenExpanded={undefined}
        data-test-uid={testCard.uid}
        data-testid="test-card"
      >
        <div className="upp-test-card__content">
          <div className="upp-test-card__journey">
            <div
              className={classNames("upp-test-card__timeline", {
                "upp-test-card__timeline--single": !testCard.nextStatus,
              })}
            >
              <span className="upp-test-card__timeline-dot upp-test-card__timeline-dot--current" />
              {testCard.nextStatus && (
                <span className="upp-test-card__timeline-dot upp-test-card__timeline-dot--next" />
              )}
            </div>
            <div className="upp-test-card__sidebar">
              <div
                className="upp-test-card__status upp-test-card__status--visible"
                data-automation_id="status_current"
              >
                <h3 className="upp-test-card__label">
                  {intl.formatMessage(messages.testCardCurrent)}
                  {" - "}
                  <span className="upp-test-card__value-status">
                    {getStatusText(
                      testCard.status,
                      intl,
                      testCard.i18nKeys?.statusText
                    )}
                  </span>
                  {renderSecondaryStatusText() && (
                    <>
                      {" "}
                      <span className="upp-test-card__value-received-date">
                        {renderSecondaryStatusText()}
                      </span>
                    </>
                  )}
                </h3>
                {testCard.description && (
                  <div className="upp-test-card__description">
                    {testCard.description}
                  </div>
                )}
              </div>
              <div
                className={classNames(
                  "upp-test-card__status upp-test-card__status--next",
                  {
                    "upp-test-card__status--visible": testCard.nextStatus,
                  }
                )}
                data-automation_id="status_next"
              >
                <div className="upp-test-card__label">
                  {testCard.nextStatus && (
                    <>
                      {intl.formatMessage(messages.testCardNext)}
                      {" - "}
                      <span className="upp-test-card__value">
                        {getStatusText(
                          testCard.nextStatus,
                          intl,
                          testCard.i18nKeys?.nextStatus
                        )}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="upp-test-card__actions">
            {renderViewDetails()}
            {showPrimaryAction && renderPrimaryAction()}
          </div>
        </div>
      </BaseTestCard>
    </div>
  );
};

export default TestCard;
